import { createFileRoute } from '@tanstack/react-router'
import { ArrowDown, ArrowUp, CheckCircle2, Edit2, Plus, Save, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { supabase } from '@/integrations/supabase/client'

type Plan = {
  id: string
  name: string
  description: string
  price: number
  max_os_per_month: number
  max_whatsapp_msgs_per_month: number
  has_financial_module: boolean
  has_ai_growth_dashboard: boolean
  features_json: string[]
  status: string
}

export const Route = createFileRoute('/master/plans')({
  component: MasterPlans,
})

function MasterPlans() {
  const [plans, setPlans] = useState<Plan[]>([])
  const [originalPlans, setOriginalPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [message, setMessage] = useState('')

  const fetchPlans = useCallback(async () => {
    const { data, error } = await supabase
      .from('system_plans')
      .select('*')
      .order('price', { ascending: true })

    if (data && !error) {
      setPlans(data as Plan[])
      setOriginalPlans(JSON.parse(JSON.stringify(data)))
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchPlans()
  }, [fetchPlans])

  const updatePlan = (index: number, field: keyof Plan, value: any) => {
    if (!isEditing) return
    const newPlans = [...plans]
    newPlans[index] = { ...newPlans[index], [field]: value }
    setPlans(newPlans)
  }

  const handleFeatureChange = (planIndex: number, featureIndex: number, value: string) => {
    if (!isEditing) return
    const newPlans = [...plans]
    newPlans[planIndex].features_json[featureIndex] = value
    setPlans(newPlans)
  }

  const addFeature = (planIndex: number) => {
    if (!isEditing) return
    const newPlans = [...plans]
    newPlans[planIndex].features_json.push('Novo recurso')
    setPlans(newPlans)
  }

  const removeFeature = (planIndex: number, featureIndex: number) => {
    if (!isEditing) return
    const newPlans = [...plans]
    newPlans[planIndex].features_json.splice(featureIndex, 1)
    setPlans(newPlans)
  }

  const moveFeature = (planIndex: number, featureIndex: number, direction: 'up' | 'down') => {
    if (!isEditing) return
    const newPlans = [...plans]
    const features = newPlans[planIndex].features_json

    if (direction === 'up' && featureIndex > 0) {
      // Troca com o de cima
      const temp = features[featureIndex]
      features[featureIndex] = features[featureIndex - 1]
      features[featureIndex - 1] = temp
    } else if (direction === 'down' && featureIndex < features.length - 1) {
      // Troca com o de baixo
      const temp = features[featureIndex]
      features[featureIndex] = features[featureIndex + 1]
      features[featureIndex + 1] = temp
    }

    setPlans(newPlans)
  }

  const handleCancel = () => {
    setPlans(JSON.parse(JSON.stringify(originalPlans)))
    setIsEditing(false)
    setMessage('')
  }

  const savePlans = async () => {
    setSaving(true)
    setMessage('')

    try {
      for (const plan of plans) {
        const { error } = await supabase
          .from('system_plans')
          .update({
            name: plan.name,
            description: plan.description,
            price: plan.price,
            max_os_per_month: plan.max_os_per_month,
            max_whatsapp_msgs_per_month: plan.max_whatsapp_msgs_per_month,
            has_financial_module: plan.has_financial_module,
            has_ai_growth_dashboard: plan.has_ai_growth_dashboard,
            features_json: plan.features_json,
          })
          .eq('id', plan.id)

        if (error) throw error
      }
      setOriginalPlans(JSON.parse(JSON.stringify(plans)))
      setIsEditing(false)
      setMessage('Planos salvos com sucesso!')
      setTimeout(() => setMessage(''), 3000)
    } catch (err: any) {
      setMessage(`Erro ao salvar: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="p-8 text-slate-400">Carregando planos...</div>
  }

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Planos do Sistema</h1>
          <p className="text-slate-400">
            Configure os limites e preços que aparecem na Landing Page.
          </p>
        </div>
        <div className="flex items-center gap-4">
          {message && (
            <span
              className={
                message.includes('Erro')
                  ? 'text-red-400 font-medium'
                  : 'text-emerald-400 font-medium'
              }
            >
              {message}
            </span>
          )}

          {!isEditing ? (
            <Button
              onClick={() => setIsEditing(true)}
              className="bg-slate-800 hover:bg-slate-700 text-white"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Editar Planos
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                onClick={handleCancel}
                disabled={saving}
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                <X className="w-4 h-4 mr-2" />
                Cancelar
              </Button>
              <Button
                onClick={savePlans}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Salvando...' : 'Confirmar Alterações'}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((plan, planIndex) => (
          <Card
            key={plan.id}
            className={`bg-slate-900 border-slate-800 transition-all ${isEditing ? 'ring-2 ring-blue-500/20' : ''}`}
          >
            <CardHeader className="pb-4">
              <CardTitle className="text-xl capitalize flex justify-between items-center text-white">
                {plan.id}
                <span className="text-xs bg-slate-800 text-slate-300 px-3 py-1 rounded-full font-medium">
                  ID: {plan.id}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-1.5">
                <Label className="text-slate-400">Nome de Exibição</Label>
                <Input
                  value={plan.name}
                  onChange={(e) => updatePlan(planIndex, 'name', e.target.value)}
                  disabled={!isEditing}
                  className="bg-slate-950 border-slate-800 text-slate-100 disabled:opacity-100 disabled:bg-slate-900"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-400">Descrição</Label>
                <Textarea
                  value={plan.description}
                  onChange={(e) => updatePlan(planIndex, 'description', e.target.value)}
                  disabled={!isEditing}
                  className="bg-slate-950 border-slate-800 h-20 resize-none text-slate-100 disabled:opacity-100 disabled:bg-slate-900"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-400">Preço Mensal (R$)</Label>
                <Input
                  type="number"
                  value={plan.price}
                  onChange={(e) => updatePlan(planIndex, 'price', parseFloat(e.target.value))}
                  disabled={!isEditing}
                  className="bg-slate-950 border-slate-800 text-xl font-bold text-blue-400 disabled:opacity-100 disabled:bg-slate-900"
                />
              </div>

              <div className="pt-5 border-t border-slate-800">
                <h4 className="text-sm font-semibold text-slate-300 mb-3">Limites do Sistema</h4>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-400">OS por mês (-1 = Infit.)</Label>
                    <Input
                      type="number"
                      value={plan.max_os_per_month}
                      onChange={(e) =>
                        updatePlan(planIndex, 'max_os_per_month', parseInt(e.target.value, 10))
                      }
                      disabled={!isEditing}
                      className="bg-slate-950 border-slate-800 h-9 text-slate-100 disabled:opacity-100 disabled:bg-slate-900"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-400">Msgs WhatsApp</Label>
                    <Input
                      type="number"
                      value={plan.max_whatsapp_msgs_per_month}
                      onChange={(e) =>
                        updatePlan(
                          planIndex,
                          'max_whatsapp_msgs_per_month',
                          parseInt(e.target.value, 10)
                        )
                      }
                      disabled={!isEditing}
                      className="bg-slate-950 border-slate-800 h-9 text-slate-100 disabled:opacity-100 disabled:bg-slate-900"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label
                    className={`flex items-center gap-3 text-sm ${isEditing ? 'cursor-pointer text-slate-200' : 'cursor-default text-slate-400'}`}
                  >
                    <input
                      type="checkbox"
                      checked={plan.has_financial_module}
                      onChange={(e) =>
                        updatePlan(planIndex, 'has_financial_module', e.target.checked)
                      }
                      disabled={!isEditing}
                      className="rounded bg-slate-950 border-slate-700 text-blue-600 focus:ring-blue-600 disabled:opacity-60 h-4 w-4"
                    />
                    Acesso ao Financeiro (Caixa)
                  </label>
                  <label
                    className={`flex items-center gap-3 text-sm ${isEditing ? 'cursor-pointer text-slate-200' : 'cursor-default text-slate-400'}`}
                  >
                    <input
                      type="checkbox"
                      checked={plan.has_ai_growth_dashboard}
                      onChange={(e) =>
                        updatePlan(planIndex, 'has_ai_growth_dashboard', e.target.checked)
                      }
                      disabled={!isEditing}
                      className="rounded bg-slate-950 border-slate-700 text-blue-600 focus:ring-blue-600 disabled:opacity-60 h-4 w-4"
                    />
                    Acesso ao AI Growth Dashboard
                  </label>
                </div>
              </div>

              <div className="pt-5 border-t border-slate-800">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-slate-300">Itens da Landing Page</h4>
                  {isEditing && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => addFeature(planIndex)}
                      className="h-7 px-2 hover:bg-slate-800 text-blue-400"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Adicionar
                    </Button>
                  )}
                </div>
                <div className="space-y-2.5">
                  {plan.features_json.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center gap-3">
                      <CheckCircle2
                        className={`w-4 h-4 shrink-0 ${isEditing ? 'text-emerald-500' : 'text-slate-500'}`}
                      />
                      <Input
                        value={feature}
                        onChange={(e) =>
                          handleFeatureChange(planIndex, featureIndex, e.target.value)
                        }
                        disabled={!isEditing}
                        className="bg-slate-950 border-slate-800 h-9 text-sm text-slate-100 disabled:opacity-100 disabled:bg-slate-900"
                      />
                      {isEditing && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={featureIndex === 0}
                            onClick={() => moveFeature(planIndex, featureIndex, 'up')}
                            className="h-8 w-8 p-0 text-slate-500 hover:text-white hover:bg-slate-800 disabled:opacity-30"
                          >
                            <ArrowUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={featureIndex === plan.features_json.length - 1}
                            onClick={() => moveFeature(planIndex, featureIndex, 'down')}
                            className="h-8 w-8 p-0 text-slate-500 hover:text-white hover:bg-slate-800 disabled:opacity-30"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFeature(planIndex, featureIndex)}
                            className="h-8 w-8 p-0 text-slate-500 hover:text-red-400 hover:bg-red-400/10 ml-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
